import type { ReactElement, RefObject } from 'react'
import type { TFunction } from 'i18next'
import { AlertCircle, CheckCircle, FileJson, Loader2, Upload, X } from 'lucide-react'
import type { ImportResponse } from '../../../types'

const BYTES_PER_KIB = 1024

const JSON_FORMAT_EXAMPLE = `{
  "criteria": [
    {
      "organisationId": "uuid",
      "externalReference": "INV-001",
      "seatsGranted": 25,
      "volumeGrantedGb": 100,
      "validFrom": "2025-01-01",
      "validUntil": "2026-01-01",
      "accessTier": "professional",
      "featuresGranted": ["api_access", "priority_support"],
      "seatAssignments": {}
    }
  ]
}`

interface DropZoneProps {
  isDragging: boolean
  selectedFile: File | null
  fileInputRef: RefObject<HTMLInputElement>
  onDragOver: (e: React.DragEvent) => void
  onDragLeave: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  t: TFunction<'admin'>
}

export function DropZone(props: DropZoneProps): ReactElement {
  const {
    isDragging,
    selectedFile,
    fileInputRef,
    onDragOver,
    onDragLeave,
    onDrop,
    onFileSelect,
    t,
  } = props
  const dropClass = isDragging
    ? 'border-[#8a9bba] bg-[#4e5e82]/5 dark:border-[#4e5e82] dark:bg-[#8a9bba]/10'
    : 'border-stone-300 hover:border-stone-400 dark:border-stone-600 dark:hover:border-stone-500'
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`relative rounded-lg border-2 border-dashed p-8 text-center transition-colors ${dropClass}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        onChange={onFileSelect}
        className="absolute inset-0 cursor-pointer opacity-0"
      />
      {selectedFile ? <SelectedFileView file={selectedFile} /> : <EmptyDropPrompt t={t} />}
    </div>
  )
}

function SelectedFileView({ file }: { file: File }): ReactElement {
  return (
    <div className="flex flex-col items-center">
      <FileJson className="h-12 w-12 text-[#5c6e94] dark:text-[#8a9bba]" />
      <p className="mt-2 font-medium text-stone-900 dark:text-white">{file.name}</p>
      <p className="text-sm text-stone-500 dark:text-stone-400">
        {(file.size / BYTES_PER_KIB).toFixed(1)} KB
      </p>
    </div>
  )
}

function EmptyDropPrompt({ t }: { t: TFunction<'admin'> }): ReactElement {
  return (
    <div className="flex flex-col items-center">
      <Upload className="h-12 w-12 text-stone-400 dark:text-stone-500" />
      <p className="mt-2 font-medium text-stone-700 dark:text-stone-300">
        {t('licensing.dragDropJson')}
      </p>
      <p className="text-sm text-stone-500 dark:text-stone-400">{t('licensing.orClickToBrowse')}</p>
    </div>
  )
}

interface ActionButtonsProps {
  selectedFile: File | null
  isUploading: boolean
  onUpload: () => void
  onClear: () => void
  t: TFunction<'admin'>
}

export function ActionButtons({
  selectedFile,
  isUploading,
  onUpload,
  onClear,
  t,
}: ActionButtonsProps): ReactElement {
  return (
    <div className="mt-4 flex gap-3">
      <button
        onClick={onUpload}
        disabled={!selectedFile || isUploading}
        className="flex items-center gap-2 rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isUploading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('licensing.importing')}
          </>
        ) : (
          <>
            <Upload className="h-4 w-4" />
            {t('licensing.import')}
          </>
        )}
      </button>
      {selectedFile && (
        <button
          onClick={onClear}
          disabled={isUploading}
          className="flex items-center gap-2 rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-700"
        >
          <X className="h-4 w-4" />
          {t('licensing.clear')}
        </button>
      )}
    </div>
  )
}

export function ErrorAlert({ message }: { message: string }): ReactElement {
  return (
    <div className="mt-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
      <AlertCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
      <p className="text-sm text-red-700 dark:text-red-300">{message}</p>
    </div>
  )
}

interface SuccessResultProps {
  result: ImportResponse
  t: TFunction<'admin'>
}

export function SuccessResult({ result, t }: SuccessResultProps): ReactElement {
  return (
    <div className="mt-4 rounded-lg border border-[#4e5e82]/15 bg-[#4e5e82]/5 p-4 dark:border-[#4e5e82] dark:bg-[#8a9bba]/10">
      <div className="flex items-start gap-3">
        <CheckCircle className="h-5 w-5 shrink-0 text-[#5c6e94] dark:text-[#8a9bba]" />
        <div className="flex-1">
          <p className="font-medium text-[#5c6e94] dark:text-[#8a9bba]">{result.message}</p>
          <ResultStats result={result} t={t} />
        </div>
      </div>
    </div>
  )
}

function ResultStats({ result, t }: SuccessResultProps): ReactElement {
  return (
    <div className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
      <StatCell
        label={`${t('licensing.processed')}:`}
        value={result.result.recordsProcessed}
        valueClass="font-medium text-stone-700 dark:text-stone-300"
      />
      <StatCell
        label={`${t('licensing.imported')}:`}
        value={result.result.recordsImported}
        valueClass="font-medium text-[#5c6e94] dark:text-[#8a9bba]"
      />
      <StatCell
        label={`${t('licensing.failed')}:`}
        value={result.result.recordsFailed}
        valueClass={`font-medium ${result.result.recordsFailed > 0 ? 'text-red-600 dark:text-red-400' : 'text-stone-700 dark:text-stone-300'}`}
      />
    </div>
  )
}

interface StatCellProps {
  label: string
  value: number
  valueClass: string
}

function StatCell({ label, value, valueClass }: StatCellProps): ReactElement {
  return (
    <div>
      <span className="text-stone-500 dark:text-stone-400">{label}</span>{' '}
      <span className={valueClass}>{value}</span>
    </div>
  )
}

export function FormatHelp({ t }: { t: TFunction<'admin'> }): ReactElement {
  return (
    <details className="mt-4">
      <summary className="cursor-pointer text-sm font-medium text-stone-600 hover:text-stone-900 dark:text-stone-400 dark:hover:text-stone-200">
        {t('licensing.viewExpectedJsonFormat')}
      </summary>
      <pre className="mt-2 overflow-x-auto rounded-lg bg-stone-100 p-4 text-xs text-stone-700 dark:bg-stone-900 dark:text-stone-300">
        {JSON_FORMAT_EXAMPLE}
      </pre>
    </details>
  )
}
